import { useState, useEffect, useMemo } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { getArticles, updateArticle, deleteArticle } from '../api/knowledge';
import { toast } from 'sonner';
import CreateArticleModal from '../components/knowledge/CreateArticleModal';
import EditArticleModal from '../components/knowledge/EditArticleModal';
import FloatingHelpButton from '../components/knowledge/FloatingHelpButton';
import {
  FileText,
  Plus,
  Search,
  Eye,
  ThumbsUp,
  Clock,
  CheckCircle2,
  CircleDashed,
  X,
  PenTool,
  Trash2,
  Send
} from 'lucide-react';

const MyArticles = () => {
  const { user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('published'); // 'published' | 'draft'
  
  // Modal States
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editArticle, setEditArticle] = useState(null);
  const [previewArticle, setPreviewArticle] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (user?.id) {
      fetchMyArticles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const fetchMyArticles = async () => {
    try {
      setLoading(true);
      const response = await getArticles({ 
        author_id: user?.id,
        limit: 100 
      });
      if (response.success) {
        setArticles(response.data?.articles || []);
      }
    } catch (error) {
      console.error('Failed to fetch my articles:', error);
      toast.error('Failed to load your articles');
    } finally {
      setLoading(false);
    }
  };

  const filteredArticles = useMemo(() => {
    let filtered = articles;

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      filtered = filtered.filter(a => 
        a.title.toLowerCase().includes(lower) || 
        (a.content && a.content.toLowerCase().includes(lower))
      );
    }

    if (activeTab === 'published') {
      return filtered.filter(a => a.is_published);
    }
    return filtered.filter(a => !a.is_published);
  }, [articles, searchTerm, activeTab]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const handlePublishDraft = async (article) => {
    try {
      setActionLoading(true);
      const res = await updateArticle(article.id, { is_published: true });
      if (res.success) {
        toast.success('Article published!');
        fetchMyArticles();
      } else {
        toast.error(res.message || 'Failed to publish');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to publish');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteArticle = async (article) => {
    try {
      setActionLoading(true);
      const res = await deleteArticle(article.id);
      if (res.success) {
        toast.success('Article deleted');
        setDeleteConfirm(null);
        fetchMyArticles();
      } else {
        toast.error(res.message || 'Failed to delete');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <DashboardLayout userRole={user?.role}>
      <div className="min-h-screen bg-gray-50/50 dark:bg-slate-950 pb-20">
        
        {/* ================= PREMIUM HEADER SECTION ================= */}
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-700 shadow-lg shadow-indigo-900/10">
          <div className="w-full max-w-none px-4 sm:px-6 lg:px-8 py-5">
            
            {/* Top Row: Title, Search, Create Button */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5">
              
              {/* Left: Brand */}
              <div className="flex items-center gap-3 min-w-max">
                <div className="p-2 bg-white/10 rounded-lg backdrop-blur-sm border border-white/10 text-white">
                  <FileText className="w-6 h-6" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white leading-tight">My Articles</h1>
                  <p className="text-blue-200 text-xs hidden sm:block">Manage & track your content</p>
                </div>
              </div>

              {/* Center/Right: Search & Create Button */}
              <div className="flex items-center gap-3 w-full md:w-auto flex-1 md:justify-end">
                {/* Search */}
                <div className="w-full md:max-w-md relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-blue-300" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search articles..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 bg-white/10 border border-white/10 rounded-xl text-sm text-white placeholder-blue-200 focus:bg-white focus:text-gray-900 focus:placeholder-gray-400 focus:border-white focus:ring-2 focus:ring-white/20 transition-all shadow-sm outline-none"
                  />
                </div>

                {/* Create Button */}
                <button
                  onClick={() => setIsCreateModalOpen(true)}
                  className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-bold shadow-lg shadow-emerald-900/20 transition-all hover:scale-105"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Write</span>
                </button>
              </div>
            </div>

            {/* Bottom Row: Tabs (Published/Drafts) */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
              <button
                onClick={() => setActiveTab('published')}
                className={`flex-shrink-0 px-4 py-2 rounded-lg text-xs font-bold transition-all border ${
                  activeTab === 'published'
                    ? 'bg-white text-indigo-700 border-white shadow-md'
                    : 'bg-white/10 text-blue-100 border-transparent hover:bg-white/20'
                }`}
              >
                Published
              </button>
              <button
                onClick={() => setActiveTab('draft')}
                className={`flex-shrink-0 px-4 py-2 rounded-lg text-xs font-bold transition-all border ${
                  activeTab === 'draft'
                    ? 'bg-white text-indigo-700 border-white shadow-md'
                    : 'bg-white/10 text-blue-100 border-transparent hover:bg-white/20'
                }`}
              >
                Drafts
              </button>
            </div>
          </div>
        </div>

        {/* ================= CONTENT AREA ================= */}
        <div className="w-full max-w-none px-4 sm:px-6 lg:px-8 mt-8 space-y-6">

          {/* Articles List */}
          <div className="space-y-4">
            {loading ? (
              <div className="p-12 text-center text-gray-500 dark:text-gray-400">Loading articles...</div>
            ) : filteredArticles.length === 0 ? (
              <div className="p-12 text-center bg-white dark:bg-slate-900 rounded-2xl border border-dashed border-gray-300 dark:border-slate-800">
                <div className="w-16 h-16 bg-gray-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">No articles found</h3>
                <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                  {searchTerm ? "Try adjusting your search terms" : "Get started by writing your first article"}
                </p>
              </div>
            ) : (
              filteredArticles.map((article) => (
                <div 
                  key={article.id}
                  className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-all group"
                >
                  <div className="flex items-start gap-4">
                    {/* Status Icon */}
                    <div className={`p-3 rounded-xl flex-shrink-0 ${
                      article.is_published 
                        ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400' 
                        : 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400'
                    }`}>
                      {article.is_published ? <CheckCircle2 className="w-6 h-6" /> : <CircleDashed className="w-6 h-6" />}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 line-clamp-1 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                          {article.title}
                        </h3>
                        
                        {/* Action Buttons */}
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setPreviewArticle(article)}
                            className="p-2 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
                            title="Preview Article"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => setEditArticle(article)}
                            className="p-2 text-gray-400 hover:text-amber-600 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors"
                            title="Edit Article"
                          >
                            <PenTool className="w-5 h-5" />
                          </button>
                          {!article.is_published && (
                            <button
                              onClick={() => handlePublishDraft(article)}
                              disabled={actionLoading}
                              className="p-2 text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors disabled:opacity-60"
                              title="Publish draft"
                            >
                              <Send className="w-5 h-5" />
                            </button>
                          )}
                          <button
                            onClick={() => setDeleteConfirm(article)}
                            className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                            title="Delete Article"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>

                      <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-4">
                        {article.short_description || article.content}
                      </p>

                      {/* Stats Row */}
                      <div className="flex flex-wrap items-center gap-4 text-xs font-medium text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-slate-800 pt-4">
                        <span className="flex items-center gap-1.5">
                          <Clock className="w-4 h-4" />
                          {formatDate(article.created_at)}
                        </span>
                        
                        {/* Category Pill */}
                        {article.category_name && (
                          <span className="px-2.5 py-1 rounded-md bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300">
                            {article.category_name}
                          </span>
                        )}

                        <div className="flex-1"></div>

                        {/* Metrics */}
                        <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400">
                          <Eye className="w-3.5 h-3.5" />
                          {article.views || 0} views
                        </span>
                        <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400">
                          <ThumbsUp className="w-3.5 h-3.5" />
                          {article.helpful_count || 0} helpful
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Modals */}
        <CreateArticleModal 
          isOpen={isCreateModalOpen} 
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={() => {
            fetchMyArticles();
            setIsCreateModalOpen(false);
          }}
          allowDraft={true}
        />

        <EditArticleModal
          isOpen={!!editArticle}
          onClose={() => setEditArticle(null)}
          onSuccess={() => {
            fetchMyArticles();
            setEditArticle(null);
          }}
          article={editArticle}
        />

        {/* Delete confirmation */}
        {deleteConfirm && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setDeleteConfirm(null)}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-800 p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Delete article?</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                “{deleteConfirm.title}” will be permanently deleted. This cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-xl font-medium"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteArticle(deleteConfirm)}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium disabled:opacity-70"
                >
                  {actionLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Preview Modal */}
        {previewArticle && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in">
            <div className="bg-white dark:bg-slate-900 w-full max-w-3xl rounded-2xl shadow-2xl max-h-[90vh] flex flex-col">
              <div className="p-6 border-b border-gray-100 dark:border-slate-800 flex justify-between items-start bg-gray-50/50 dark:bg-slate-900/50">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    {previewArticle.title}
                  </h2>
                  <div className="flex gap-3 text-xs text-gray-500 dark:text-gray-400">
                    <span>{formatDate(previewArticle.created_at)}</span>
                    <span>•</span>
                    <span>{previewArticle.category_name || 'Uncategorized'}</span>
                  </div>
                </div>
                <button 
                  onClick={() => setPreviewArticle(null)}
                  className="p-2 hover:bg-gray-200 dark:hover:bg-slate-800 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                </button>
              </div>
              <div className="p-8 overflow-y-auto prose dark:prose-invert max-w-none">
                {/* Simple rendering of content - assumes plain text or simple HTML */}
                <div className="whitespace-pre-wrap text-gray-700 dark:text-gray-300 leading-relaxed">
                  {previewArticle.content}
                </div>
              </div>
              <div className="p-4 border-t border-gray-100 dark:border-slate-800 flex justify-end">
                <button 
                  onClick={() => setPreviewArticle(null)}
                  className="px-4 py-2 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-700 font-medium"
                >
                  Close Preview
                </button>
              </div>
            </div>
          </div>
        )}

        <FloatingHelpButton />
      </div>
    </DashboardLayout>
  );
};

export default MyArticles;
